import { useAtom } from "jotai";
import { useRef } from "react";
import type { CurrentMfe } from "../components/Router/state";
import { getMountId } from "../utils";
import {
  timeToFirstMfeLoadHasCompletedAtom,
  traceManager,
} from "./traceManager";

const startFetchMfeTrace = (
  mfeName: string,
  overrideExistingTrace?: boolean
) => {
  traceManager.start(
    "fetch-mfe",
    {
      parentSpan: traceManager.getTransaction("TimeToFirstMFELoad") ?? null,
      attributes: { mfe: mfeName },
    },
    overrideExistingTrace
  );
};

const endTraces = () => {
  traceManager.endTraces("fetch-mfe", "TimeToFirstMFELoad");
};

/**
 * Used when the MFE name is known
 */
export const useTimeToFirstMfeLoadTrace = (mfeName: string) => {
  const [hasCompleted, setHasCompleted] = useAtom(
    timeToFirstMfeLoadHasCompletedAtom
  );
  if (hasCompleted) return () => undefined;

  startFetchMfeTrace(mfeName);

  return () => {
    endTraces();
    setHasCompleted(true);
  };
};

/**
 * Used in the Router.tsx component
 */
export const useTimeToFirstMfeLoadRouterTrace = (
  currentMfe: CurrentMfe | null
) => {
  const [hasCompleted, setHasCompleted] = useAtom(
    timeToFirstMfeLoadHasCompletedAtom
  );
  const hasInitedRef = useRef<boolean>(false);
  const mountIdRef = useRef<string>("");

  if (hasCompleted) return () => undefined;

  if (currentMfe && mountIdRef.current !== getMountId(currentMfe)) {
    startFetchMfeTrace(currentMfe.name, true);

    mountIdRef.current = getMountId(currentMfe);
    hasInitedRef.current = true;
  }

  return (mountId: string) => {
    if (mountId === mountIdRef.current) {
      endTraces();
    } else {
      traceManager.cancelAll();
    }
    setHasCompleted(true);
  };
};
